/**
 * Admin → Lifecycle: the check-in emails that are due next (B-968, B-7211).
 *
 * Since B-7211 these send automatically once an hour (LIFECYCLE_AUTO_SEND), so
 * this screen is a preview of what goes out next plus a way to send one early.
 *
 * The shape of this screen IS the safety mechanism. Nothing here fires on
 * load, nothing is pre-selected, and there is no "send to all" button — you
 * read who is due and why, open the actual body, tick the rows you agree with,
 * and confirm a count. The backend re-checks eligibility for every item at
 * send time and caps the batch, so a stale tab can't re-send to somebody who
 * was already mailed.
 *
 * B-976: the preview started life as a hand-rolled overlay with its own
 * `max-h-[80vh]` scroller, which meant it missed the shared Modal's fix for
 * short viewports (a tall body pushing the footer, Close included, off-screen)
 * and had no Escape key, no backdrop semantics and no focus affordances. It now
 * uses the shared `Modal`, so the panel is capped to the viewport, the body
 * scrolls inside it and Close stays pinned. `/admin` renders outside the V2
 * shell, which is fine — the colour tokens are defined on `:root` in
 * v2-tokens.css (only typography is scoped to `.v2-root`), and that stylesheet
 * loads on every route.
 */
import React, { useEffect, useMemo, useState } from 'react';

import { BackendAPI } from '../../lib/api';
import { Modal } from '../../v2/components/ui/modal';
import { Button } from '../../v2/components/ui/button';

type DueRow = Awaited<ReturnType<typeof BackendAPI.adminLifecycleDue>>['due'][number];

/** Human labels for the template ids the API returns. */
const TEMPLATE_LABEL: Record<string, string> = {
  lc_upload_failed: 'Upload failed: offer to run it',
  lc_not_exported: 'Read fine, never downloaded',
  lc_no_upload: 'Signed up, never uploaded',
  lc_feedback: 'Converted: ask for feedback',
  lc_checkin: 'Second check-in',
  lc_last: 'Last note',
};

const key = (r: { user_id: string; template: string }) => `${r.user_id}::${r.template}`;

/** Admin tab: lifecycle email preview + explicit send. */
const LifecycleTab: React.FC = () => {
  const [rows, setRows] = useState<DueRow[]>([]);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<DueRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    BackendAPI.adminLifecycleDue()
      .then((res) => {
        setRows(res.due);
        setEmailEnabled(res.email_enabled);
        // Selection is never carried across a reload: the list it referred to
        // is gone, and silently keeping ticks is how you send the wrong email
        // to the wrong person.
        setSelected(new Set());
      })
      .catch((e) => setError(e?.message || 'Failed to load the lifecycle queue'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = (r: DueRow) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const k = key(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const chosen = useMemo(() => rows.filter((r) => selected.has(key(r))), [rows, selected]);

  const send = async () => {
    if (!chosen.length) return;
    const names = chosen.map((r) => r.email).join('\n');
    if (!confirm(`Send ${chosen.length} email${chosen.length === 1 ? '' : 's'} now?\n\n${names}`))
      return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const res = await BackendAPI.adminLifecycleSend(
        chosen.map((r) => ({ user_id: r.user_id, template: r.template })),
      );
      const notSent = res.results.filter((r) => r.status !== 'sent');
      setResult(
        `Sent ${res.sent}, skipped ${res.skipped}, failed ${res.failed}.` +
          (notSent.length
            ? ` Not sent: ${notSent.map((s) => `${s.user_id} (${s.detail})`).join('; ')}`
            : ''),
      );
      load();
    } catch (e: any) {
      setError(e?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        Check-in emails that are due next, each written for what actually happened
        to that person. They send automatically once an hour; open a row to read the
        exact email, or tick rows to send them now. Anyone already sent this message,
        or who has unsubscribed, never appears here.
      </div>

      {!emailEnabled && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          Email delivery is <strong>not configured</strong> on this environment
          (<code>EMAIL_PROVIDER=none</code>). Sending will fail until it is.
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
          onClick={send}
          disabled={sending || !chosen.length}
        >
          {sending ? 'Sending…' : `Send ${chosen.length || ''} selected`}
        </button>
        <span className="text-sm text-muted-foreground">{rows.length} due</span>
      </div>

      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      {result && <p className="text-sm text-muted-foreground">{result}</p>}

      {!loading && !rows.length && (
        <p className="text-sm text-muted-foreground">
          Nobody is due a check-in right now. That&rsquo;s a normal answer.
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="w-10 p-2" />
                <th className="p-2">Who</th>
                <th className="p-2">Email</th>
                <th className="p-2">Why</th>
                <th className="w-24 p-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={key(r)} className="border-t border-border">
                  <td className="p-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.email}`}
                      checked={selected.has(key(r))}
                      onChange={() => toggle(r)}
                    />
                  </td>
                  <td className="p-2">{r.email}</td>
                  <td className="p-2">{TEMPLATE_LABEL[r.template] || r.template}</td>
                  <td className="p-2 text-muted-foreground">{r.reason}</td>
                  <td className="p-2">
                    <button
                      type="button"
                      className="rounded-md border border-border px-2 py-1 text-xs"
                      onClick={() => setPreview(r)}
                    >
                      Preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <Modal
          label="Email preview"
          title={preview.subject || '(no subject)'}
          subtitle={`To: ${preview.email}`}
          onClose={() => setPreview(null)}
          testId="lifecycle-preview"
          className="max-w-2xl"
          footer={
            <Button variant="secondary" size="sm" onClick={() => setPreview(null)}>
              Close
            </Button>
          }
        >
          {/* The email is authored for a mail client: white background, dark
              inline-styled text. Give it that surface rather than letting it
              land unreadably on the dark V2 card. */}
          <div
            className="rounded-[var(--r-md)] bg-white text-black"
            dangerouslySetInnerHTML={{ __html: preview.html || '' }}
          />
        </Modal>
      )}
    </div>
  );
};

export default LifecycleTab;
